import { authFetch } from "./authClient";
import { setUnlockedKey } from "./vault";

// ── Helpers (reuse vault.ts patterns) ────────────────────────────────

const PBKDF2_ITERATIONS = 250_000;

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

async function deriveWrappingKey(userId: string, saltB64: string): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(new TextEncoder().encode(userId)),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: toArrayBuffer(fromBase64(saltB64)),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["wrapKey", "unwrapKey"],
  );
}

async function wrapKey(masterKey: CryptoKey, wrappingKey: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.wrapKey("raw", masterKey, wrappingKey, { name: "AES-GCM", iv });
  // Encode as: base64(iv) + "." + base64(wrappedKey)
  return `${toBase64(iv)}.${toBase64(new Uint8Array(wrapped))}`;
}

async function unwrapKey(encoded: string, wrappingKey: CryptoKey): Promise<CryptoKey> {
  const [ivB64, wrappedB64] = encoded.split(".");
  if (!ivB64 || !wrappedB64) throw new Error("Invalid encrypted vault key format");
  return crypto.subtle.unwrapKey(
    "raw",
    toArrayBuffer(fromBase64(wrappedB64)),
    wrappingKey,
    { name: "AES-GCM", iv: toArrayBuffer(fromBase64(ivB64)) },
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
}

// ── Public API ───────────────────────────────────────────────────────

interface UserProfile {
  vaultSalt: string | null;
  encryptedVaultKey: string | null;
}

/**
 * Initialize the account-derived vault for the signed-in workspace.
 * On first login: generates a new master key, wraps it, stores on server.
 * On subsequent logins: unwraps the existing master key.
 * In both cases, injects the key into vault.ts via setUnlockedKey().
 */
export async function initAccountVault(userId: string, profile: UserProfile): Promise<void> {
  if (!profile.vaultSalt || !profile.encryptedVaultKey) {
    await createAccountVault(userId);
  } else {
    await unlockAccountVault(userId, profile.vaultSalt, profile.encryptedVaultKey);
  }
}

async function createAccountVault(userId: string): Promise<void> {
  const salt = toBase64(crypto.getRandomValues(new Uint8Array(16)));

  // Generate random master key
  const masterKey = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true, // extractable — needed for wrapping
    ["encrypt", "decrypt"],
  );

  // Wrap master key with account-derived wrapping key
  const wrappingKey = await deriveWrappingKey(userId, salt);
  const encryptedVaultKey = await wrapKey(masterKey, wrappingKey);

  // Store on server
  const response = await authFetch("/account/profile", {
    method: "PUT",
    body: JSON.stringify({ vaultSalt: salt, encryptedVaultKey }),
  });
  if (!response.ok) throw new Error("Failed to store vault key");

  // Inject into vault.ts
  setUnlockedKey(masterKey);
}

async function unlockAccountVault(userId: string, salt: string, encryptedVaultKey: string): Promise<void> {
  const wrappingKey = await deriveWrappingKey(userId, salt);
  const masterKey = await unwrapKey(encryptedVaultKey, wrappingKey);
  setUnlockedKey(masterKey);
}
